import { useEffect, useState } from 'react';

/**
 * Hook for managing launch session countdown
 * @param {string} gameId - The game ID to track
 * @returns {object} - { session, remaining, isActive, isExpired }
 */
export default function useLaunchCountdown(gameId) {
  const [session, setSession] = useState(null);
  const [remaining, setRemaining] = useState('');

  useEffect(() => {
    if (!gameId) return;

    let intervalId;

    const updateCountdown = () => {
      // Check localStorage for cached session data
      const cachedSession = localStorage.getItem(`launchSession_${gameId}`);
      if (cachedSession) {
        try {
          const sessionData = JSON.parse(cachedSession);
          setSession(sessionData);

          if (sessionData.expiresAt) {
            const now = Date.now();
            const timeLeft = sessionData.expiresAt - now;

            if (timeLeft > 0) {
              // Format remaining time as HH:MM:SS
              const hours = Math.floor(timeLeft / (1000 * 60 * 60));
              const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
              const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

              const formatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
              setRemaining(formatted);
            } else {
              setRemaining('00:00:00');
            }
          }
        } catch (error) {
          console.error('Error parsing cached session:', error);
          localStorage.removeItem(`launchSession_${gameId}`);
        }
      }
    };

    // Update immediately
    updateCountdown();

    // Update every second
    intervalId = setInterval(updateCountdown, 1000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [gameId]);

  const isActive = session && session.expiresAt && session.expiresAt > Date.now();
  const isExpired = session && session.expiresAt && session.expiresAt <= Date.now();

  return {
    session,
    remaining,
    isActive,
    isExpired
  };
}